// Copyright (c) 2016 Deere & Company

import React, {PropTypes} from 'react';
import {
    getVsrMessage,
    hasVsrErrorForClaimField,
    isResponsibleAccountEditable
} from '../../../../shared/services/claim-shared-helper';
import AmountOfUse from '../../../../shared/components/claim-fields/amount-of-use/amount-of-use-without-compliance';
import ClaimMaintenancePlan from '@deere/cps-it.warranty-model-factory/lib/claim-maintenance-plan';
import Dealer from '@deere/cps-it.warranty-model-factory/lib/dealer';
import FormValidation from '../../state/form-validation';
import {FormattedMessage} from 'react-intl';
import KeyValuePair from '@deere/cps-it.warranty-model-factory/lib/key-value-pair';
import LabeledDateInput from '../../../../shared/components/claim-fields/labeled-date-input';
import LabeledInput from '../../../../shared/components/claim-fields/labeled-input';
import LabeledInputWithBlur from '../../../../shared/components/claim-fields/labeled-input-with-blur';
import LabeledKeyValueDropDown from '../../../../shared/components/claim-fields/labeled-key-value-dropdown';
import Product from '@deere/cps-it.warranty-model-factory/lib/product';
import ResponsibleAccounts from '../../../../shared/components/claim-fields/responsible-accounts';
import {canGetMaintenancePlans} from '../../services/claim-validation-helper';
import {fixCursorPosition} from '../../../../../shared/services/fix-cursor-position';
import {formatPin} from '../../../../../shared/services/format-pin';
import {isValidDate} from '../../../../../shared/services/build-date';
import moment from 'moment';

function handleInputBlur(property, value) {

    this.actions.updateFormValidation(property, value);

}

function handleDateBlur(property, date) {

    const modifiedDate = isValidDate(date.target.value) ? moment.utc(date.target.value).valueOf() : null;

    this.actions.updateFormValidation(property, modifiedDate);

}

function handleResponsibleAccountChange(accountId) {

    this.actions.updateResponsibleAccount(accountId, this.selectedAccounts);

}

function resetCustomerProductDetails() {

    this.actions.resetCkcCustomerFailure();
    this.actions.resetCustomerDetails();
    this.actions.resetProductDetails();

}

function handlePinBlur(pin) {

    this.actions.updateFormValidation('pin', pin);

    if (pin) {

        if (pin !== this.product.pin) {

            this::resetCustomerProductDetails();
            this.actions.readClaimProduct(pin);

        }

    } else {

        this::resetCustomerProductDetails();

    }

}

function handlePinChange(pin, event) {

    const formattedPin = formatPin(pin);

    this.actions.updatePin(formattedPin);
    fixCursorPosition(event, formattedPin);

}

function handleWorkOrderOneChange(workOrderOne) {

    this.actions.updateWorkOrderOne(workOrderOne);

}

function handleWorkOrderTwoChange(workOrderTwo) {

    this.actions.updateWorkOrderTwo(workOrderTwo);

}

function handleRepairNumberChange(repairNumber) {

    this.actions.updateRepairNumber(repairNumber);

}

function handleServiceDateChange(date) {

    this.actions.updateServiceDate(date);

}

function handleUseAmountChange(amount) {

    this.actions.updateUseAmount(amount);

}

function handleUseAmountUnitOptionChange(key) {

    this.actions.updateUseAmountUnit(key, this.product.useAmountAllowedUnits);

}

function handleGoodsInstallationVatChange(event) {

    this.actions.updateGoodsInstallationVat(event.target.value, this.goodsInstallationVatOptions);

}

function handleButtonClick() {

    this.actions.readOpenMaintenancePlans(this.claim, false);

}

const ProductInformationContainer = props => {

    const canEditResponsibleAccount = isResponsibleAccountEditable(props.isEditable, props.claim.claimStatus);

    return (
        <section className='product-information-container'>
            <h3>
                <FormattedMessage id='PRODUCT_INFORMATION'/>
            </h3>
            <div className='product-info'>
                <ResponsibleAccounts
                    dealers={props.selectedAccounts}
                    errorMessage='DEALER_LOCATION_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'responsibleAccount', canEditResponsibleAccount)}
                    isEditable={canEditResponsibleAccount}
                    isValid={props.formValidation.responsibleAccount}
                    onOptionBlur={handleInputBlur.bind(props, 'responsibleAccount')}
                    onOptionChange={props::handleResponsibleAccountChange}
                    selectedAccount={props.claim.responsibleAccount}
                    vsrMessage={getVsrMessage(props.claim.errors, 'responsibleAccount', canEditResponsibleAccount)}
                />
                <LabeledInputWithBlur
                    className='pin'
                    errorMessage='PIN_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'pin', props.isEditable)}
                    isEditable={props.isEditable && (props.claim.claimStatus.key === 'ZDFT' || !props.claim.claimStatus.key)}
                    isValid={props.formValidation.pin}
                    label='PIN'
                    maxLength={18}
                    onBlur={props::handlePinBlur}
                    onChange={props::handlePinChange}
                    value={props.claim.pin}
                    vsrMessage={getVsrMessage(props.claim.errors, 'pin', props.isEditable)}
                />
            </div>
            <h3>
                <FormattedMessage id='REFERENCE_INFORMATION'/>
            </h3>
            <div className='reference-info'>
                <LabeledInputWithBlur
                    className='work-order-one'
                    errorMessage='WORK_ORDER_ONE_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'workOrderOne', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.referenceNumber1}
                    label='WORK_ORDER_ONE'
                    maxLength={26}
                    onBlur={handleInputBlur.bind(props, 'referenceNumber1')}
                    onChange={props::handleWorkOrderOneChange}
                    value={props.claim.referenceInfo.referenceNumber1}
                    vsrMessage={getVsrMessage(props.claim.errors, 'workOrderOne', props.isEditable)}
                />
                <LabeledInput
                    className='work-order-two'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'workOrderTwo', props.isEditable)}
                    isEditable={props.isEditable}
                    label='WORK_ORDER_TWO'
                    maxLength={12}
                    onChange={props::handleWorkOrderTwoChange}
                    value={props.claim.referenceInfo.referenceNumber2}
                    vsrMessage={getVsrMessage(props.claim.errors, 'workOrderTwo', props.isEditable)}
                />
                <LabeledInput
                    className='repair-number'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'repairNumber', props.isEditable)}
                    isEditable={props.isEditable}
                    label='REPAIR_NUMBER'
                    maxLength={13}
                    onChange={props::handleRepairNumberChange}
                    value={props.claim.referenceInfo.repairNumber}
                    vsrMessage={getVsrMessage(props.claim.errors, 'repairNumber', props.isEditable)}
                />
                <LabeledDateInput
                    className='service-date'
                    date={props.claim.serviceDate}
                    errorMessage='SERVICE_DATE_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'serviceDate', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.serviceDate}
                    label='SERVICED'
                    maxDate={moment()}
                    onBlur={handleDateBlur.bind(props, 'serviceDate')}
                    onDateChange={props::handleServiceDateChange}
                    vsrMessage={getVsrMessage(props.claim.errors, 'serviceDate', props.isEditable)}
                />
            </div>
            <div className='reference-info'>
                <AmountOfUse
                    allowedUnits={props.product.useAmountAllowedUnits}
                    className='amount-of-use'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'amountOfUse', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.useAmount && props.formValidation.useAmountUnit}
                    onInputBlur={handleInputBlur.bind(props, 'useAmount')}
                    onInputChange={props::handleUseAmountChange}
                    onOptionBlur={handleInputBlur.bind(props, 'useAmountUnit')}
                    onOptionChange={props::handleUseAmountUnitOptionChange}
                    selectedUnit={props.claim.useAmount.unit}
                    showBlankOption={false}
                    value={props.claim.useAmount.amount}
                    vsrMessage={getVsrMessage(props.claim.errors, 'amountOfUse', props.isEditable)}
                />
                {props.claim.showGoodsInstallationVat &&
                    <LabeledKeyValueDropDown
                        className='goods-installation-vat'
                        errorMessage='GOODS_INSTALLATION_VAT_IS_REQUIRED'
                        highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'goodsInstallationVat', props.isEditable)}
                        isEditable={props.isEditable}
                        isValid={true}
                        label='GOODS_INSTALLATION_VAT'
                        list={props.goodsInstallationVatOptions}
                        onChange={props::handleGoodsInstallationVatChange}
                        selectedValue={props.claim.goodsInstallationVat}
                        vsrMessage={getVsrMessage(props.claim.errors, 'goodsInstallationVat', props.isEditable)}
                    />
                }
            </div>
            {props.isEditable &&
                <div className='button-group'>
                    <button
                        disabled={!canGetMaintenancePlans(props.claim)}
                        onClick={props::handleButtonClick}
                    >
                        <FormattedMessage id='GET_MAINTENANCE_PLANS'/>
                    </button>
                </div>
            }
        </section>
    );

};

ProductInformationContainer.displayName = 'ProductInformationContainer';
ProductInformationContainer.propTypes = {
    actions: PropTypes.object.isRequired,
    claim: PropTypes.instanceOf(ClaimMaintenancePlan).isRequired,
    formValidation: PropTypes.instanceOf(FormValidation).isRequired,
    goodsInstallationVatOptions: PropTypes.arrayOf(PropTypes.instanceOf(KeyValuePair)).isRequired,
    isEditable: PropTypes.bool.isRequired,
    product: PropTypes.instanceOf(Product).isRequired,
    selectedAccounts: PropTypes.arrayOf(PropTypes.instanceOf(Dealer)).isRequired
};

export default ProductInformationContainer;



// WEBPACK FOOTER //
// ./src/claim/marketing/maintenance-plan/components/product-information/product-information-container.js